import React from "react";
import { Box, Container, Typography, Card, CardContent } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { motion } from "framer-motion";
import { RecordVoiceOver, Forum, AdminPanelSettings } from "@mui/icons-material";

const interactionFeatures = [
  {
    title: "Capture Evangelism Where It Happens",
    desc: "Record meaningful conversations while the details are still fresh so useful context does not disappear after the outreach ends.",
    icon: <RecordVoiceOver sx={{ fontSize: 40, color: "#4ade80" }} />,
  },
  {
    title: "Connect Every Kind of Outreach",
    desc: "Bring conversations, events, messages, flyers, direct mail and other evangelistic activity into the same shared history.",
    icon: <Forum sx={{ fontSize: 40, color: "#4ade80" }} />,
  },
  {
    title: "Give People the Right Level of Access",
    desc: "Let volunteers contribute useful information without requiring sensitive ministry data to be visible to everyone.",
    icon: <AdminPanelSettings sx={{ fontSize: 40, color: "#4ade80" }} />,
  },
];

const InteractionsSection = () => {
  return (
    <Box
      sx={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
        py: { xs: 10, md: 14 },
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <Typography
            variant="h3"
            textAlign="center"
            sx={{ color: "#ffffff", fontWeight: 800, mb: 2 }}
          >
            Turn Individual Interactions into a Shared Picture
          </Typography>

          <Typography
            variant="h6"
            textAlign="center"
            sx={{
              color: "rgba(255,255,255,0.7)",
              maxWidth: 760,
              mx: "auto",
              mb: 6,
            }}
          >
            Your volunteers may each see one conversation. PATHWAYS helps the
            evangelism lead see what the whole team is learning across hundreds
            of interactions.
          </Typography>
        </motion.div>

        <Grid2 container spacing={2}>
          {interactionFeatures.map((feature, index) => (
            <Grid2 size={{ xs: 12, md: 4 }} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card
                  sx={{
                    background: "#1e293b",
                    textAlign: "center",
                    p: 3,
                    borderRadius: 4,
                    boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
                    height: "100%",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 10px 28px rgba(0,0,0,0.35)",
                      transition: "all 0.3s ease",
                    },
                  }}
                >
                  <Box>{feature.icon}</Box>
                  <CardContent>
                    <Typography
                      variant="h6"
                      sx={{ color: "#ffffff", fontWeight: 700 }}
                      gutterBottom
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.7)" }}
                    >
                      {feature.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid2>
          ))}
        </Grid2>

        <Box
          sx={{
            position: "relative",
            mt: { xs: 8, md: 12 },
            height: { xs: 520, sm: 600, md: 460 },
            mx: "auto",
          }}
        >
          <Box
            component={motion.img}
            src="/Features/Features-Interactions-Left.JPG"
            alt="Interactions overview"
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            sx={{
              position: "absolute",
              borderRadius: 3,
              boxShadow: "0 30px 80px rgba(0,0,0,0.45)",
              left: { xs: "10%", md: "0%" },
              top: { xs: "0%", md: "0%" },
              width: { xs: "80%", md: "40%" },
              zIndex: { xs: 1, md: 1 },
              opacity: { xs: 1, md: 0.88 },
              border: "1px solid rgba(255,255,255,0.12)",
              backgroundColor: "rgba(255,255,255,0.02)",
            }}
          />

          <Box
            component={motion.img}
            src="/Features/Features-Interactions-Center.JPG"
            alt="Interactions overview"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            sx={{
              position: "absolute",
              borderRadius: 3,
              boxShadow: "0 40px 120px rgba(0,0,0,0.6)",
              left: { xs: "10%", md: "25%" },
              top: { xs: "30%", md: "20%" },
              width: { xs: "80%", md: "50%" },
              zIndex: 3,
              border: "1px solid rgba(255,255,255,0.14)",
              backgroundColor: "rgba(255,255,255,0.02)",
            }}
          />

          <Box
            component={motion.img}
            src="/Features/Features-Interactions-Right.JPG"
            alt="Interactions overview"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            sx={{
              position: "absolute",
              borderRadius: 3,
              boxShadow: "0 30px 80px rgba(0,0,0,0.45)",
              left: { xs: "10%", md: "60%" },
              top: { xs: "60%", md: "0%" },
              width: { xs: "80%", md: "40%" },
              zIndex: { xs: 2, md: 1 },
              opacity: { xs: 1, md: 0.88 },
              border: "1px solid rgba(255,255,255,0.12)",
              backgroundColor: "rgba(255,255,255,0.02)",
            }}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default InteractionsSection;